import * as React from "react";
import { Alert, AlertTitle, Box } from "@mui/material";
import "./Alert.css";
import { apiErrorStr } from "../../helpers";

interface AlertInfo {
  id?: number;
  title?: string;
  body?: string;
  severity?: "error" | "warning" | "info" | "success";
  dismissAfterMs?: number;
}

export interface MyAlertsRef {
  addApiErrorAlert: (e: any) => void;
  addApiSuccessAlert: (body: string, opts?: Partial<AlertInfo>) => void;
  addAlert: (alert: AlertInfo) => void;
}

const MyAlerts = React.forwardRef<MyAlertsRef>((_, ref) => {
  const [alerts, setAlerts] = React.useState<Array<AlertInfo>>([]);
  const nextIdRef = React.useRef(1);

  const dismissAlert = React.useCallback((id: number) => {
    setAlerts((prevAlerts) => prevAlerts.filter((a) => a.id !== id));
  }, []);

  const addAlert = React.useCallback(
    (alert: AlertInfo) => {
      const id = nextIdRef.current++;
      const newAlert = { ...alert, id };
      setAlerts((prevAlerts) => [...prevAlerts, newAlert]);

      if (alert.dismissAfterMs) {
        setTimeout(() => {
          dismissAlert(id);
        }, alert.dismissAfterMs);
      }
    },
    [dismissAlert],
  );

  // convenience
  const addApiErrorAlert = React.useCallback(
    (e: any) => {
      addAlert({
        title: "Error",
        severity: "error",
        body: apiErrorStr(e),
      });
    },
    [addAlert],
  );

  const addApiSuccessAlert = React.useCallback(
    (body: string, opts?: Partial<AlertInfo>) => {
      addAlert({
        title: "Success!",
        severity: "success",
        dismissAfterMs: 5000,
        body,
        ...opts,
      });
    },
    [addAlert],
  );

  React.useImperativeHandle(
    ref,
    () => ({
      addApiErrorAlert,
      addApiSuccessAlert,
      addAlert,
    }),
    [addApiErrorAlert, addApiSuccessAlert, addAlert],
  );

  return (
    <div className="sticky-alert">
      <Box sx={{ position: "absolute", left: 0, right: 0 }}>
        {alerts.map((alert) => (
          <Alert
            key={`alert_${alert.id}`}
            severity={alert.severity || "info"}
            onClose={() => alert.id && dismissAlert(alert.id)}
            sx={{ mb: 1 }}
          >
            {alert.title && <AlertTitle>{alert.title}</AlertTitle>}
            {alert.body}
          </Alert>
        ))}
      </Box>
    </div>
  );
});

MyAlerts.displayName = "MyAlerts";

export default MyAlerts;
